import { useEffect, useState, type PropsWithChildren } from "react";

import { useAuth } from "@/components/AuthProvider";
import { HubPresenceProvider } from "@/components/HubPresenceProvider";
import { HubSessionConnectingScreen } from "@/components/HubSessionConnectingScreen";
import { startHubDbPoolHolderStream } from "@/lib/hubDbPoolHolderStream";
import { startHubLifecycleStream } from "@/lib/hubLifecycleStream";
import { ensureHubPublicConfig } from "@/lib/hubPublicConfig";
import {
  isHubSessionLiveStreamOpen,
  onHubSessionLiveStreamOpen,
} from "@/lib/hubSessionLive";

/** Visible failure copy only. Does not skip the gate or start chrome GETs. */
export const HUB_SESSION_LIVE_CONNECT_FAIL_MS = 8_000;

/**
 * Hard refresh / new tab: holder stream is the first /api call. Chrome GETs and
 * presence POST wait until that stream is actually open. SPA navigation skips
 * the wait when this tab already has the stream.
 */
export function HubSessionLiveGate({ children }: PropsWithChildren) {
  const { session } = useAuth();
  const enabled = session != null && session.user?.id != null && session.user.id !== "";
  const [streamOpen, setStreamOpen] = useState(() => !enabled || isHubSessionLiveStreamOpen());
  const [connectFailed, setConnectFailed] = useState(false);

  useEffect(() => {
    if (!enabled) {
      setStreamOpen(true);
      setConnectFailed(false);
      return;
    }
    const offOpen = onHubSessionLiveStreamOpen(() => {
      setStreamOpen(true);
      setConnectFailed(false);
    });
    const failTimer = window.setTimeout(() => {
      if (!isHubSessionLiveStreamOpen()) {
        setConnectFailed(true);
      }
    }, HUB_SESSION_LIVE_CONNECT_FAIL_MS);
    let stopHolders: (() => void) | null = null;
    let cancelled = false;
    void ensureHubPublicConfig().then(() => {
      if (cancelled) {
        return;
      }
      stopHolders = startHubDbPoolHolderStream();
    });
    return () => {
      cancelled = true;
      offOpen();
      window.clearTimeout(failTimer);
      stopHolders?.();
    };
  }, [enabled, session?.user?.id]);

  useEffect(() => {
    if (!enabled || !streamOpen) {
      return;
    }
    return startHubLifecycleStream();
  }, [enabled, streamOpen]);

  if (!enabled) {
    return <>{children}</>;
  }

  if (!streamOpen) {
    return <HubSessionConnectingScreen failed={connectFailed} />;
  }

  return <HubPresenceProvider>{children}</HubPresenceProvider>;
}
